/**
 * The main product walkthrough, mirroring the flow the Navattic demo used.
 *
 * Shape: dashboard -> feature -> back to dashboard -> next feature, so the dashboard
 * renders several times and must be well covered by the snapshot.
 */

import type { TourDefinition } from '@/components/tour/tour.types';

const NEXT = [{ label: 'Next', type: 'next' as const }];

/**
 * Opens the marketing site in a new tab, where the Get Demo and Get Started Free
 * buttons live. utm_source tags the visit as coming from the demo, replacing the
 * utm_source=navattic the old tool used.
 */
const CTA_URL = 'https://app.resinsmart.ai/free-trial?utm_source=demo';

const BENCHMARK_URL = 'https://resinsmart.ai/benchmark?utm_source=demo';

export const RESINSMART_OVERVIEW: TourDefinition = {
  slug: 'resinsmart-overview',
  title: 'Explore the ResinSmart Platform',
  steps: [
    {
      id: 'welcome',
      name: 'Demo Start',
      targetRoute: '/dashboard',
      // Collapses the nav again when stepping back from the left-nav step.
      preActions: [
        {
          type: 'click',
          selector: '[data-tour="left-nav-toggle"]',
          onlyIfElementExists: '[data-tour="left-nav"].w-64',
        },
      ],
      overlays: [
        {
          id: 'ov-welcome',
          type: 'modal',
          title: 'Explore the ResinSmart Platform',
          body: `
            <p class="text-rs-green"><strong><em>The data displayed in this walkthrough is for demo purposes only, and is not an accurate representation of the current market.</em></strong></p>
            <p>This quick tour will walk you through ResinSmart’s core features—built to
            give resin buyers an edge. On average, buyers save 19% in their first year,
            with over $1B saved on our platform so far.</p>
            <p>Click into any area to see:</p>
            <ul>
              <li>What it does</li>
              <li>Why it matters</li>
              <li>How it helps you buy smarter</li>
            </ul>
            <p>Let’s get started.</p>
          `,
          actions: [
            { label: 'Start The Tour', type: 'next' },
            { label: 'Start Free Trial', type: 'cta', href: CTA_URL },
          ],
        },
      ],
    },
    {
      id: 'minimize-hint',
      name: 'How to minimize',
      targetRoute: '/dashboard',
      overlays: [
        {
          id: 'ov-minimize-hint',
          type: 'modal',
          highlightsMinimizeButton: true,
          title: 'Want a closer look?',
          body: `
            <p>Use the <strong>−</strong> button in the top corner to tuck this card out
            of the way and explore the page on your own.</p>
            <p>A <em>Resume tour</em> button takes its place, so you can pick up right
            where you left off.</p>
          `,
          actions: NEXT,
        },
      ],
    },
    {
      id: 'left-nav',
      name: 'Left navigation',
      targetRoute: '/dashboard',
      // Opens the nav so every option is on show, just for this step.
      preActions: [
        {
          type: 'click',
          selector: '[data-tour="left-nav-toggle"]',
          onlyIfElementExists: '[data-tour="left-nav"].w-16',
        },
      ],
      overlays: [
        {
          id: 'ov-left-nav',
          type: 'tooltip',
          anchor: { selector: '[data-tour="left-nav"]' },
          placement: 'right',
          title: 'Everything in one place',
          body: `
            <p>Export analysis, pricing, and current market data — all reachable from
            here, resin by resin.</p>
            <p>Jump straight to the dashboard, snapshot, or forecast you need without
            digging through reports.</p>
          `,
          actions: NEXT,
        },
      ],
    },
    {
      id: 'pricing-forecast-tile',
      name: 'Pricing & Forecast tile',
      targetRoute: '/dashboard',
      // Closes the nav again for the rest of the tour.
      preActions: [
        {
          type: 'click',
          selector: '[data-tour="left-nav-toggle"]',
          onlyIfElementExists: '[data-tour="left-nav"].w-64',
        },
      ],
      overlays: [
        {
          id: 'ov-pricing-tile',
          type: 'tooltip',
          anchor: { selector: '[data-testid="pricing-forecast-button"]' },
          placement: 'bottom',
          title: 'Pricing & Forecast',
          body: '<p><em>Price clarity for today. Forecasts for what’s next.</em></p>',
          actions: NEXT,
        },
      ],
    },
    {
      id: 'forecast-insights',
      name: 'Quarterly forecast insights',
      targetRoute: '/forecast',
      overlays: [
        {
          id: 'ov-forecast-insights',
          type: 'tooltip',
          anchor: { selector: '[data-tour="forecast-expand-all"]' },
          placement: 'left',
          title: 'Your early warning system for pricing shifts',
          body: `
            <p>Our pricing forecasts are informed by 3B+ lbs of real transaction data,
            key market signals, and insider intel from resin experts.</p>
            <ul>
              <li>Updated quarterly to reflect the latest trends</li>
              <li>Built to help you time buys, defend budgets, and negotiate contracts</li>
              <li>Trusted by top resin buyers to navigate market swings with confidence</li>
            </ul>
            <p><em>Available for Compound &amp; Prime users</em></p>
          `,
          actions: NEXT,
        },
      ],
    },
    {
      id: 'fair-price',
      name: 'Fair price chart',
      targetRoute: '/forecast',
      overlays: [
        {
          id: 'ov-fair-price',
          type: 'tooltip',
          anchor: { selector: '[data-tour="fair-price-chart"]' },
          placement: 'right',
          title: 'Know the Right Price—Right Now',
          body: `
            <p>Get an unbiased view of what you should be paying, based on 3B+ pounds in
            real buyer transactions—not supplier narratives.</p>
            <ul>
              <li>See our “Fair Price” (attainable mid-sized buyer-paid price)</li>
              <li>Use it to negotiate better deals, validate quotes, or push back with proof</li>
            </ul>
            <p><em>Available for Compound &amp; Prime users. Also, talk to us about how to
            access our “Best Price” (what the best buyers are achieving).</em></p>
          `,
          actions: NEXT,
        },
      ],
    },
    {
      id: 'weekly-drivers-tile',
      name: 'Weekly drivers tile',
      targetRoute: '/dashboard',
      overlays: [
        {
          id: 'ov-weekly-drivers-tile',
          type: 'tooltip',
          anchor: { selector: '[data-testid="weekly-drivers-button"]' },
          placement: 'bottom',
          title: 'Weekly Drivers',
          body: '<p><em>Weekly market insights and trends</em></p>',
          actions: NEXT,
        },
      ],
    },
    {
      id: 'weekly-drivers',
      name: 'Weekly drivers',
      targetRoute: '/drivers',
      overlays: [
        {
          id: 'ov-weekly-drivers',
          type: 'modal',
          title: 'Weekly Drivers',
          body: `
            <p>Every week, we decode what’s moving the market—supply shifts, price
            pressure, production changes, and more.</p>
            <ul>
              <li>Includes 30/60/90-day buying recommendations so you can act, not react.</li>
              <li>Use it to guide short-term strategy or sanity-check what your supplier
              is telling you.</li>
            </ul>
          `,
          actions: NEXT,
        },
      ],
    },
    {
      id: 'monthly-snapshot-tile',
      name: 'Monthly Snapshot tile',
      targetRoute: '/dashboard',
      overlays: [
        {
          id: 'ov-snapshot-tile',
          type: 'tooltip',
          anchor: { selector: '[data-testid="monthly-snapshot-button"]' },
          placement: 'bottom',
          title: 'Monthly Snapshot',
          body: '<p><em>Monthly market wrap — at a glance</em></p>',
          actions: NEXT,
        },
      ],
    },
    {
      id: 'monthly-snapshot',
      name: 'Monthly snapshot page',
      targetRoute: '/snapshot/pe',
      overlays: [
        {
          id: 'ov-snapshot',
          type: 'modal',
          title: 'Monthly Snapshot',
          body: `
            <p>Quickly brief your team—or yourself—with a clear summary of last month’s
            market activity.</p>
            <ul>
              <li>Production</li>
              <li>Inventories</li>
              <li>Trade flows</li>
              <li>Price trends</li>
            </ul>
            <p>All visual, all digestible, all in one place. Great for exec teams,
            internal updates, and board meetings.</p>
          `,
          actions: NEXT,
        },
      ],
    },
    {
      id: 'market-intel-tile',
      name: 'Market Intel tile',
      targetRoute: '/dashboard',
      overlays: [
        {
          id: 'ov-intel-tile',
          type: 'tooltip',
          anchor: { selector: '[data-testid="market-intel-button"]' },
          placement: 'bottom',
          title: 'Market Intel',
          body: '<p><em>Go deep with Market Intel for each resin type</em></p>',
          actions: NEXT,
        },
      ],
    },
    {
      id: 'market-intel',
      name: 'Market Intel dashboard',
      targetRoute: '/marketIntel/pe',
      overlays: [
        {
          id: 'ov-intel',
          type: 'modal',
          title: 'Market Intel',
          body: `
            <p>When you need to go beyond headlines, our dashboards give you real-time
            visibility into:</p>
            <ul>
              <li>Supply &amp; demand</li>
              <li>Operating rates &amp; inventory</li>
              <li>Feedstock trends &amp; trade flows</li>
            </ul>
            <p>Slice, compare, and customize. It’s the intel you’d build yourself—if you
            had a team of resin analysts.</p>
          `,
          actions: NEXT,
        },
      ],
    },
    {
      id: 'inventory-change',
      name: 'Inventory change tab',
      targetRoute: '/marketIntel/pe',
      // Below 1100px only three metric tabs render at a time, so step the arrow across
      // until this one exists before clicking it.
      // When navigating back from Operating Rates, clicking "Next" moved
      // in the wrong direction, skipped past the target, and left the tour stuck
      // on Total Sales. Try "Previous" first to ensure we move back correctly.
      preActions: [
        {
          type: 'click',
          selector: '[aria-label="Previous metric"]',
          backupSelector: '[aria-label="Next metric"]',
          untilVisible: '[data-tour="pe-metric-inventory_change"]',
        },
      ],
      overlays: [
        {
          id: 'ov-inventory-change',
          type: 'tooltip',
          anchor: { selector: '[data-tour="pe-metric-inventory_change"]' },
          placement: 'bottom',
          title: 'Inventory Change vs. Cumulative Draws/Gains: See Where Supply Is Heading',
          body: `
            <p>See total monthly inventory changes at a glance—then drill into Cumulative
            Draws/Gains to spot sustained supply shifts.</p>
            <p><em>How buyers use it:</em> Identify when persistent draws signal tightening
            supply—or when gains could ease price pressure—so you can time buys
            accordingly.</p>
          `,
          actions: NEXT,
        },
      ],
    },
    {
      id: 'operating-rates',
      name: 'Operating rates tab',
      targetRoute: '/marketIntel/pe',
      preActions: [
        {
          type: 'click',
          selector: '[aria-label="Next metric"]',
          backupSelector: '[aria-label="Previous metric"]',
          untilVisible: '[data-tour="pe-metric-operating_rates"]',
        },
      ],
      overlays: [
        {
          id: 'ov-operating-rates',
          type: 'tooltip',
          anchor: { selector: '[data-tour="pe-metric-operating_rates"]' },
          placement: 'bottom',
          title: 'Operating Rate vs. Demand Rate: Match Supply Activity to Market Demand',
          body: `
            <p>Overlay operating rates with demand rates to see when production is
            outpacing or lagging actual market needs.</p>
            <p><em>How buyers use it:</em> Spot early signs of oversupply or shortages
            before they show up in pricing—giving you leverage in negotiations.</p>
          `,
          actions: NEXT,
        },
      ],
    },
    {
      id: 'closing',
      name: 'Closing CTA',
      targetRoute: '/marketIntel/pe',
      overlays: [
        {
          id: 'ov-closing',
          type: 'modal',
          body: `
            <div class="flex flex-col sm:flex-row gap-5">
              <div class="sm:w-1/2 flex flex-col">
                <h3 class="text-lg font-bold mb-3 text-rs-green">Ready to buy smarter?</h3>
                <p>That’s a quick look at how ResinSmart gives resin buyers an edge —
                market intel, monthly snapshots, weekly drivers, and fair price
                benchmarks in one place.</p>
                <p>Buyers save 19% on average in their first year.</p>
                <div class="mt-auto py-4">
                  <a href="${CTA_URL}" target="_blank" rel="noopener"
                     class="flex w-full items-center justify-center rounded-md bg-rs-green px-4 py-3 text-center text-sm font-semibold text-black no-underline hover:opacity-85">
                    Start Free Trial
                  </a>
                </div>
              </div>
              <div class="sm:w-1/2 sm:border-l sm:border-rs-jet-light/60 sm:pl-5 flex flex-col">
                <h3 class="text-lg font-bold mb-3 text-rs-purple">Request your benchmark</h3>
                <p>ResinSmart’s benchmark report shows how your pricing compares against
                real buyer transaction data, so you can negotiate with confidence.</p>
                <p>See how your own pricing compares against real buyer transaction data.</p>
                <div class="mt-auto py-4">
                  <a href="${BENCHMARK_URL}" target="_blank" rel="noopener"
                     class="flex w-full items-center justify-center rounded-md bg-rs-purple px-4 py-3 text-center text-sm font-semibold text-black no-underline hover:opacity-85">
                    Request Your Benchmark
                  </a>
                </div>
              </div>
            </div>
          `,
          actions: [{ label: 'Start Over', type: 'restart' }],
        },
      ],
    },
  ],
};
